import type { Id } from '@/convex/_generated/dataModel';
import type { RealtimeVoiceDelegationTarget } from '@/lib/realtime-voice-agent';
import type { RealtimeVoiceSourceThread } from '@/lib/realtime-voice-thread-dispatch';

/** Voice uses the personal chat's authoritative steering and queue decisions. */
export function createYourImaiVoiceDispatch(dependencies: {
  orgThreadId: Id<'orgThreads'>;
  organizationId: Id<'organizations'>;
  getThread: () => RealtimeVoiceSourceThread | null | undefined;
  deliver: (input: {
    orgThreadId: Id<'orgThreads'>;
    sessionId: string;
    clientInputId: string;
    prompt: string;
  }) => Promise<{ delivery: 'sent' | 'steered' | 'queued' }>;
  stop: NonNullable<RealtimeVoiceDelegationTarget['stopStreaming']>;
}): RealtimeVoiceDelegationTarget {
  const deliver: NonNullable<
    RealtimeVoiceDelegationTarget['dispatchMessage']
  > = async (prompt, _attachments, _preset, _rawPrompt, options) => {
    const thread = dependencies.getThread();
    if (
      !thread ||
      thread._id !== dependencies.orgThreadId ||
      thread.organizationId !== dependencies.organizationId ||
      thread.status === 'archived'
    )
      throw new Error('Your IMAI is unavailable');
    if (!options?.realtimeVoiceSessionId)
      throw new Error('Voice session is unavailable');
    const receipt = await dependencies.deliver({
      orgThreadId: dependencies.orgThreadId,
      sessionId: options.realtimeVoiceSessionId,
      clientInputId: `imai-voice-${crypto.randomUUID()}`,
      prompt,
    });
    return {
      status: receipt.delivery === 'queued' ? 'queued' : 'started',
      accepted: true,
    };
  };
  return {
    get pending() {
      return dependencies.getThread()?.threadStatus === 'pending';
    },
    sendMessage: deliver,
    sendMessageNow: deliver,
    dispatchMessage: deliver,
    stopStreaming: dependencies.stop,
  };
}
